/**
 * Shared parsers for Salesforce-sourced Matter fields.
 *
 * The Salesforce sync writes `case_type`, `principal_attourney`, and
 * `case_manager` as one of four shapes:
 *   1. literal `"NULL"` sentinel       → no data
 *   2. empty string / null / undefined → no data
 *   3. plain name string (e.g. "Heather Hartley")
 *   4. JSON-encoded object             `{"Name": "Heather Hartley"}`
 *   5. JSON object from the PUBLIC schema API `{ Name: "Heather Hartley" }`
 *
 * Every surface that reads these fields needs to handle all four. This
 * module is the single source of truth so surfaces don't drift.
 */

/**
 * Resolve a Matter entity field (case_type / attorney / manager) to a
 * display name, or null if no name can be extracted.
 *
 * Safe against invalid JSON — returns null instead of throwing.
 *
 * @param {string|object|null|undefined} value
 * @returns {string|null}
 */
export function resolveEntityName(value) {
  if (value == null || value === "" || value === "NULL") return null;

  if (typeof value === "object") {
    const objectName = value?.Name;
    return typeof objectName === "string" && objectName.trim()
      ? objectName.trim()
      : null;
  }

  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (!trimmed.startsWith("{")) {
    return trimmed;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return parsed?.Name || null;
  } catch (_err) {
    return null;
  }
}

/**
 * Same as {@link resolveEntityName} but returns a caller-supplied
 * fallback string (e.g. `"N/A"`, `"—"`) when no name is available.
 *
 * @param {string|null|undefined} value
 * @param {string} [fallback="N/A"]
 * @returns {string}
 */
export function resolveEntityNameOr(value, fallback = "N/A") {
  return resolveEntityName(value) ?? fallback;
}

/**
 * Case types whose matter workspace is chat-only (no "The Brief").
 *
 * VA CAVC matters hold a single Record Before the Agency PDF and no VA
 * Agency knowledge graph — the analysis surface has nothing to show, so
 * the AI panel renders chat alone with no tab strip (Matthew,
 * 2026-07-16). Compared case-insensitively against the resolved
 * Salesforce case-type name.
 */
const CHAT_ONLY_CASE_TYPES = Object.freeze(["va cavc"]);
const VA_AGENCY_CASE_TYPES = Object.freeze(["va agency"]);

/**
 * @param {string|object|null|undefined} caseTypeValue any of the shapes
 *   {@link resolveEntityName} accepts (raw string, JSON string, object).
 * @returns {boolean} true when the matter's AI panel should be chat-only.
 */
export function isChatOnlyCaseType(caseTypeValue) {
  const name = resolveEntityName(caseTypeValue);
  return !!name && CHAT_ONLY_CASE_TYPES.includes(name.trim().toLowerCase());
}

/**
 * @param {string|object|null|undefined} caseTypeValue any of the shapes
 *   {@link resolveEntityName} accepts (raw string, JSON string, object).
 * @returns {boolean} true only for VA Agency matters.
 */
export function isVaAgencyCaseType(caseTypeValue) {
  const name = resolveEntityName(caseTypeValue);
  return !!name && VA_AGENCY_CASE_TYPES.includes(name.trim().toLowerCase());
}

/**
 * Prevent actions from appearing for a previously loaded matter while Redux
 * is catching up with cross-matter navigation.
 *
 * @param {{id?: string|number, case_type?: unknown}|null|undefined} matter
 * @param {string|number|null|undefined} currentMatterId
 * @returns {boolean}
 */
export function isCurrentVaAgencyMatter(matter, currentMatterId) {
  if (matter?.id == null || currentMatterId == null) return false;
  return (
    String(matter.id) === String(currentMatterId) &&
    isVaAgencyCaseType(matter.case_type)
  );
}
